import AppState from 'AppState';
import { InputProps } from 'components/shared/edit/fields';
import ListingStatusDisplay from 'components/shared/list/ListingStatusDisplay';
import DownChevronSVG from 'components/shared/svg/DownChevronSVG';
import LightningSVG from 'components/shared/svg/LightningSVG';
import SharedSVG from 'components/shared/svg/SharedSVG';
import UpChevronSVG from 'components/shared/svg/UpChevronSVG';
import TooltipWrapper from 'components/shared/tooltip/TooltipWrapper';
import * as React from 'react';
import { Field, FieldRenderProps } from 'react-final-form';
import { Button, Label } from 'reactstrap';
import { AppCulture } from 'types';
import BaseDto from 'types/__models__/BaseDto';
import { stripHtml } from 'util/StringUtil';
import { FallbackValueDisplayMode } from '../TranslatedField';
import withEditContext, { HasEditContext } from '../withEditContext';
import RequiredFieldMarker from './RequiredFieldMarker';
import classnames from 'classnames';
import RocTitle from '../../RocTitle';
import { AvailableReplacementTokens, TokenAvailability } from 'components/shared/AvailableReplacementTokens';
import CoinStackSVG from 'components/shared/svg/CoinStackSVG';

export type FieldLabelProps = FieldRenderProps<any, HTMLInputElement> &
	InputProps &
	HasEditContext & {
		label: string;
		name: string;
		id?: string;
		subLabel?: string;
		isRequired: boolean;
		htmlFor?: string;
		children?: React.ReactNode;
		isPowerUser: boolean;
		forceEmptyLabel: boolean;
		labelElementHidden?: boolean;
		labelClassName?: string;
		isShared?: boolean;
		translatedFieldName?: string;
		primaryCulture?: AppCulture;
		fallbackValueDisplayMode?: FallbackValueDisplayMode;
		fallbackValueOverride?: string;
		dto?: BaseDto;
		/**
		 * List of available replacement tokens.
		 */
		replacementTokenAvailabilities?: TokenAvailability[];
	};

interface FieldLabelState {
	displayFallbackValue: boolean;
}

/**
 * Wrapper component for all field labels for ease of use.
 *
 * @class FieldLabel
 * @extends {React.Component<FieldRenderProps & InputProps>}
 */
class FieldLabel extends React.Component<FieldLabelProps, FieldLabelState> {
	private sharedContainerRef;

	protected static defaultProps = {
		isTooltipEditMode: false,
		hideTooltip: false,
		isShared: true,
		labelElementHidden: false,
		labelClassName: '',
	};

	private titleContainerRef;

	constructor(props) {
		super(props);

		this.sharedContainerRef = React.createRef();

		this.titleContainerRef = React.createRef();

		this.state = {
			displayFallbackValue: false,
		};
	}

	public shouldComponentUpdate(nextProps: FieldLabelProps, nextState: FieldLabelState) {
		const {
			label,
			isRequired,
			input,
			editContext: { tooltipData, hasTooltipError },
			htmlFor,
			children,
			hideTooltip,
		} = this.props;

		return (
			nextProps.dto !== this.props.dto ||
			nextProps.label !== label ||
			nextProps.isRequired !== isRequired ||
			(nextProps.input && nextProps.input.name) !== (input && input.name) ||
			nextProps.editContext.tooltipData !== tooltipData ||
			nextProps.editContext.hasTooltipError !== hasTooltipError ||
			nextProps.htmlFor !== htmlFor ||
			nextProps.children !== children ||
			nextProps.hideTooltip !== hideTooltip ||
			nextState.displayFallbackValue !== this.state.displayFallbackValue
		);
	}

	public render() {
		const {
			label,
			name,
			subLabel,
			isRequired,
			input,
			editContext: { tooltipData, hasTooltipError, entityDefinition },
			htmlFor,
			children,
			hideTooltip,
			isPowerUser,
			forceEmptyLabel,
			labelElementHidden,
			labelClassName,
			isShared,
			translatedFieldName,
			primaryCulture,
			fallbackValueDisplayMode = FallbackValueDisplayMode.Inline,
			fallbackValueOverride,
			dto,
			meta,
			translatedFieldValue,
			replacementTokenAvailabilities,
		} = this.props;

		if (!forceEmptyLabel && (!label || !label.trim())) {
			return null;
		}

		const isTooltipEditMode = AppState?.user?.isTooltipEditMode ?? false;

		let tooltipText = '';
		let nameValue = '';

		if (input && input.name) {
			nameValue = input.name;
		} else if (name) {
			nameValue = name;
		} else {
			nameValue = label.toLowerCase();
		}

		if (nameValue) {
			const fieldTooltip = tooltipData && tooltipData[nameValue] && tooltipData[nameValue].fieldTooltip;
			const universalTooltip = tooltipData && tooltipData[nameValue] && tooltipData[nameValue].universalTooltip;
			tooltipText =
				fieldTooltip && fieldTooltip.text ? fieldTooltip.text : universalTooltip ? universalTooltip.text : '';
		}

		const toggleIconClass = 'translated-field__toggle-icon';

		return (
			<>
				<Label
					className={classnames({
						'visually-hidden': labelElementHidden,
					})}
					for={htmlFor}
				>
					<span className={`form-label-icon-wrapper${forceEmptyLabel ? ' visually-hidden' : ''}`}>
						<span
							className={classnames('form-label-text', {
								[`${labelClassName}`]: labelClassName,
							})}
						>
							{label}
						</span>
						{isRequired ? <RequiredFieldMarker /> : null}
						<div className="tooltip__row">
							{dto ? (
								<div className="tooltip__item">
									<ListingStatusDisplay entityDefinition={entityDefinition} record={dto} />
								</div>
							) : null}

							{isPowerUser ? (
								<div className="tooltip__item">
									<RocTitle label="Power user field">
										<span ref={this.titleContainerRef}>
											<LightningSVG class="form-label-icon" />
										</span>
									</RocTitle>
								</div>
							) : null}

							{isShared && entityDefinition && entityDefinition.localizeMode ? (
								<>
									<RocTitle label="Shared Field" alignment="left">
										<div className="tooltip__item">
											<SharedSVG class="tooltip__edit-icon tooltip__edit-icon--big tooltip__edit-icon--primary" />
										</div>
									</RocTitle>

									<span className="visually-hidden">Shared Field</span>
								</>
							) : null}

							<TooltipWrapper
								hideTooltip={hideTooltip}
								hasTooltipError={hasTooltipError}
								tooltipText={tooltipText}
								nameValue={nameValue}
								isTooltipEditMode={isTooltipEditMode}
								tooltipData={tooltipData}
								entityDefinition={entityDefinition}
								label={this.props.label}
							/>

							{replacementTokenAvailabilities ? (
								<AvailableReplacementTokens
									availabilities={replacementTokenAvailabilities}
									renderTokens={(uniqueSortedTokens) => (
										<RocTitle
											wrap
											label={`Available Tokens: ${uniqueSortedTokens
												.map((token) => `%%${token}%%`)
												.join(' | ')}`}
										>
											<div className="tooltip__item">
												<CoinStackSVG class="tooltip__edit-icon tooltip__edit-icon--big tooltip__edit-icon--primary" />
											</div>
										</RocTitle>
									)}
								/>
							) : null}
						</div>
					</span>{' '}
					{subLabel && <div className="text-small">{subLabel}</div>}
					{children ? children : null}
				</Label>
				{translatedFieldName && fallbackValueDisplayMode !== FallbackValueDisplayMode.Hidden ? (
					fallbackValueDisplayMode === FallbackValueDisplayMode.Inline ? (
						<>
							{translatedFieldName && primaryCulture ? (
								<>
									<br />
									{primaryCulture.name} ({primaryCulture.code}):{' '}
									{!translatedFieldValue && meta.dirty ? '<No Value>' : null}
									<Field name={translatedFieldName}>
										{({ input: { value } }) =>
											fallbackValueOverride ? <>{fallbackValueOverride}</> : <>{value}</>
										}
									</Field>
								</>
							) : null}
						</>
					) : (
						<>
							{/* 
							  The fallback value toggle is generally used for fields with very large values. 
							  Out of the box this is only WYSIWYG fields 
							*/}
							<br />
							<Button
								color="link"
								onClick={() => {
									this.setState((prevState) => ({
										displayFallbackValue: !prevState.displayFallbackValue,
									}));
								}}
							>
								{this.state.displayFallbackValue ? 'Hide' : 'View'} {primaryCulture!.name} (
								{primaryCulture!.code}) Translation{' '}
								{this.state.displayFallbackValue ? (
									<UpChevronSVG class={toggleIconClass} />
								) : (
									<DownChevronSVG class={toggleIconClass} />
								)}
							</Button>
							{this.state.displayFallbackValue && translatedFieldName ? (
								<div className="translated-field__fallback">
									<Field name={translatedFieldName}>
										{({ input: { value } }) =>
											fallbackValueOverride ? (
												<>{stripHtml(fallbackValueOverride)}</>
											) : (
												<>{stripHtml(value)}</>
											)
										}
									</Field>
								</div>
							) : null}
						</>
					)
				) : null}
			</>
		);
	}
}

// wrapped component type needed for tests
export class FieldLabelWrapped extends FieldLabel {}

export default withEditContext(FieldLabel);
